// Gestão de Frota (módulo "frota", 2026-10-01) — backend
// src/fleet-management. Regras de acesso/escopo ficam no backend: cliente
// sempre recebe só a própria frota; equipe filtra por cliente (ownerUserId).
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut, gpsDriveUploadWithFields } from "@/lib/api/gpsdrive-client";

export interface FleetAccess {
  moduleEnabled: boolean;
  view: boolean;
  manage: boolean;
  scope: "company" | "own";
}

export interface FleetOwner {
  id: string;
  name: string;
  email: string;
}

export interface FleetDriver {
  id: string;
  ownerUserId: string | null;
  name: string;
  cpf: string | null;
  phone: string | null;
  email: string | null;
  cnhNumber: string | null;
  cnhCategory: string | null;
  cnhExpiresAt: string | null;
  traccarDeviceId: number | null;
  notes: string | null;
  // Perfil estilo Instagram (2026-10-14).
  bio: string | null;
  photoUrl: string | null;
  photoThumbUrl: string | null;
  bannerUrl: string | null;
  active: boolean;
  createdAt: string;
}

export const FLEET_EXPENSE_CATEGORIES = [
  { key: "combustivel", label: "Combustível", color: "#0ea5e9" },
  { key: "manutencao", label: "Manutenção", color: "#f59e0b" },
  { key: "pneus", label: "Pneus", color: "#8b5cf6" },
  { key: "multa", label: "Multas", color: "#f43f5e" },
  { key: "seguro", label: "Seguro", color: "#10b981" },
  { key: "ipva", label: "IPVA", color: "#6366f1" },
  { key: "licenciamento", label: "Licenciamento", color: "#14b8a6" },
  { key: "lavagem", label: "Lavagem", color: "#22d3ee" },
  { key: "pedagio", label: "Pedágio", color: "#eab308" },
  { key: "estacionamento", label: "Estacionamento", color: "#a3a3a3" },
  { key: "outros", label: "Outros", color: "#64748b" },
] as const;

export interface FleetExpenseCategory {
  key: string;
  label: string;
  color: string;
  // Apagada: sai da escolha, mas segue dando nome aos lançamentos antigos.
  archived: boolean;
}

export const FLEET_RECURRENCE_FREQUENCIES = [
  { key: "semanal", label: "Semanal" },
  { key: "quinzenal", label: "Quinzenal" },
  { key: "mensal", label: "Mensal" },
  { key: "bimestral", label: "Bimestral" },
  { key: "trimestral", label: "Trimestral" },
  { key: "semestral", label: "Semestral" },
  { key: "anual", label: "Anual" },
] as const;

export interface FleetExpense {
  id: string;
  ownerUserId: string | null;
  traccarDeviceId: number | null;
  driverId: string | null;
  category: string;
  description: string | null;
  amount: number;
  date: string;
  odometerKm: number | null;
  liters: number | null;
  paid: boolean;
  maintenanceId: string | null;
  recurringExpenseId: string | null;
  createdAt: string;
}

export interface FleetRecurringExpense {
  id: string;
  ownerUserId: string | null;
  traccarDeviceId: number | null;
  driverId: string | null;
  category: string;
  description: string | null;
  amount: number;
  frequency: string;
  nextDate: string;
  endDate: string | null;
  active: boolean;
}

export const FLEET_REVENUE_CATEGORIES = [
  { key: "locacao", label: "Locação", color: "#10b981" },
  { key: "frete", label: "Frete", color: "#0ea5e9" },
  { key: "servico", label: "Serviço", color: "#8b5cf6" },
  { key: "reembolso", label: "Reembolso", color: "#f59e0b" },
  { key: "venda", label: "Venda", color: "#6366f1" },
  { key: "outros", label: "Outros", color: "#64748b" },
] as const;
export type FleetRevenueCategory = (typeof FLEET_REVENUE_CATEGORIES)[number]["key"];

export interface FleetRevenue {
  id: string;
  ownerUserId: string | null;
  traccarDeviceId: number | null;
  driverId: string | null;
  category: FleetRevenueCategory;
  description: string | null;
  amount: number;
  date: string;
  received: boolean;
  createdAt: string;
}

export interface FleetMaintenance {
  id: string;
  ownerUserId: string | null;
  traccarDeviceId: number;
  type: "preventiva" | "corretiva";
  title: string;
  status: "agendada" | "concluida" | "cancelada";
  scheduledDate: string | null;
  scheduledKm: number | null;
  doneAt: string | null;
  doneKm: number | null;
  cost: number | null;
  provider: string | null;
  notes: string | null;
  // Repetição + aviso da próxima (2026-10-14).
  intervalKm: number | null;
  intervalDays: number | null;
  notifyWhatsapp: boolean;
  notifyPush: boolean;
  notifyPhone: string | null;
  notifyDaysBefore: number;
  notifyKmBefore: number;
  notifiedAt: string | null;
  nextMaintenanceId: string | null;
  // Manutenção 3D (2026-11-03): peça do carro, prioridade e fotos.
  component: string | null;
  priority: "baixa" | "media" | "alta" | null;
  photos: { key: string; url: string | null; thumbUrl: string | null }[];
  createdAt: string;
}

// Catálogo de serviços da manutenção (Configurações) — null do servidor =
// lista padrão (troca de óleo, pneus etc.).
export interface FleetMaintenanceService {
  name: string;
  type: "preventiva" | "corretiva";
  intervalKm: number | null;
  intervalDays: number | null;
}

export interface FleetRental {
  id: string;
  ownerUserId: string | null;
  traccarDeviceId: number;
  renterName: string;
  renterDocument: string | null;
  renterPhone: string | null;
  startDate: string;
  endDate: string | null;
  returnedAt: string | null;
  amount: number;
  deposit: number | null;
  startKm: number | null;
  endKm: number | null;
  status: "ativa" | "finalizada" | "cancelada";
  notes: string | null;
  createdAt: string;
}

interface DatedRef {
  id: string;
  title?: string;
  name?: string;
  traccarDeviceId?: number;
  scheduledDate?: string | null;
  cnhExpiresAt?: string | null;
}

export interface FleetSummary {
  expenses: {
    total: number;
    byCategory: Record<string, number>;
    byDevice: Record<string, number>;
    byMonth: Record<string, number>;
    fuelLiters: number;
  };
  maintenance: { scheduled: number; overdue: DatedRef[]; upcoming: DatedRef[] };
  rentals: { active: number; overdue: number; revenue: number; rentedDeviceIds: number[] };
  drivers: { active: number; cnhExpired: DatedRef[]; cnhExpiring: DatedRef[] };
}

// GET /fleet-management/stats — km/tempos da frota calculados no servidor
// (2026-10-02). "computing" = ainda calculando, consultar de novo.
export interface FleetServerStats {
  status: "ready" | "computing" | "error";
  computedAt: string | null;
  progress: { done: number; total: number } | null;
  error: string | null;
  data: {
    byVehicle: Record<string, { distanceKm: number; movingMs: number; stoppedMs: number; stops: number }>;
    days: { day: string; km: number; movingMs: number; stoppedMs: number }[];
  } | null;
}

// "" = tudo da empresa, "empresa" = sem cliente, uuid = aquele cliente.
// Cliente logado: o backend ignora e usa ele mesmo.
export type FleetOwnerFilter = string;

const qs = (owner: FleetOwnerFilter, extra: Record<string, string | undefined> = {}) => {
  const params = new URLSearchParams();
  if (owner) params.set("ownerUserId", owner);
  for (const [k, v] of Object.entries(extra)) if (v) params.set(k, v);
  const s = params.toString();
  return s ? `?${s}` : "";
};

const BASE = "/api/v1/fleet-management";

function crud<T>(path: string) {
  return {
    list: (owner: FleetOwnerFilter, extra?: Record<string, string | undefined>) =>
      gpsDriveGet<T[]>(`${BASE}/${path}${qs(owner, extra)}`),
    create: (owner: FleetOwnerFilter, body: Partial<T>) => gpsDrivePost<T>(`${BASE}/${path}${qs(owner)}`, body),
    update: (owner: FleetOwnerFilter, id: string, body: Partial<T>) =>
      gpsDrivePut<T>(`${BASE}/${path}/${id}${qs(owner)}`, body),
    remove: (owner: FleetOwnerFilter, id: string) => gpsDriveDelete<void>(`${BASE}/${path}/${id}${qs(owner)}`),
  };
}

export const fleetManagementService = {
  access: () => gpsDriveGet<FleetAccess>(`${BASE}/access`),
  owners: () => gpsDriveGet<FleetOwner[]>(`${BASE}/owners`),
  vehicles: (owner: FleetOwnerFilter) => gpsDriveGet<{ deviceIds: number[] }>(`${BASE}/vehicles${qs(owner)}`),
  // deviceId = Painel filtrado num veículo só.
  summary: (owner: FleetOwnerFilter, from: string, to: string, deviceId?: string) =>
    gpsDriveGet<FleetSummary>(`${BASE}/summary${qs(owner, { from, to, deviceId })}`),
  stats: (owner: FleetOwnerFilter, from: string, to: string, deviceId?: string) =>
    gpsDriveGet<FleetServerStats>(`${BASE}/stats${qs(owner, { from, to, deviceId })}`),
  drivers: crud<FleetDriver>("drivers"),
  expenses: crud<FleetExpense>("expenses"),
  revenues: crud<FleetRevenue>("revenues"),
  maintenances: crud<FleetMaintenance>("maintenances"),
  rentals: crud<FleetRental>("rentals"),

  // Foto / banner do perfil do motorista (2026-10-14).
  uploadMaintenancePhoto: (owner: FleetOwnerFilter, id: string, file: File) =>
    gpsDriveUploadWithFields<FleetMaintenance>(`${BASE}/maintenances/${id}/photos${qs(owner)}`, file, {}),
  removeMaintenancePhoto: (owner: FleetOwnerFilter, id: string, key: string) =>
    gpsDriveDelete<FleetMaintenance>(`${BASE}/maintenances/${id}/photos${qs(owner, { key })}`),
  uploadDriverImage: (owner: FleetOwnerFilter, driverId: string, kind: "photo" | "banner", file: File) =>
    gpsDriveUploadWithFields<FleetDriver>(`${BASE}/drivers/${driverId}/image${qs(owner, { kind })}`, file, {}),
  removeDriverImage: (owner: FleetOwnerFilter, driverId: string, kind: "photo" | "banner") =>
    gpsDriveDelete<FleetDriver>(`${BASE}/drivers/${driverId}/image${qs(owner, { kind })}`),
  // Seta do motorista (2026-10-04): documentos, contratos e recorrência.
  driverDocuments: (owner: FleetOwnerFilter, driverId: string) =>
    gpsDriveGet<FleetDriverDocument[]>(`${BASE}/drivers/${driverId}/documents${qs(owner)}`),
  uploadDriverDocument: (owner: FleetOwnerFilter, driverId: string, file: File, category: string) =>
    gpsDriveUploadWithFields<FleetDriverDocument>(`${BASE}/drivers/${driverId}/documents${qs(owner)}`, file, { category }),
  deleteDriverDocument: (owner: FleetOwnerFilter, driverId: string, id: string) =>
    gpsDriveDelete<void>(`${BASE}/drivers/${driverId}/documents/${id}${qs(owner)}`),
  // Configurações da frota (por dono — nunca as da empresa): modelos de
  // contrato e chave Asaas pra cobrar os motoristas.
  contractTemplates: (owner: FleetOwnerFilter) => gpsDriveGet<FleetContractTemplate[]>(`${BASE}/contract-templates${qs(owner)}`),
  createContractTemplate: (owner: FleetOwnerFilter, body: { title: string; content: string }) =>
    gpsDrivePost<FleetContractTemplate>(`${BASE}/contract-templates${qs(owner)}`, body),
  updateContractTemplate: (owner: FleetOwnerFilter, id: string, body: { title: string; content: string }) =>
    gpsDrivePut<FleetContractTemplate>(`${BASE}/contract-templates/${id}${qs(owner)}`, body),
  deleteContractTemplate: (owner: FleetOwnerFilter, id: string) => gpsDriveDelete<void>(`${BASE}/contract-templates/${id}${qs(owner)}`),
  settings: (owner: FleetOwnerFilter) => gpsDriveGet<FleetSettings>(`${BASE}/settings${qs(owner)}`),
  recurringExpenses: (owner: FleetOwnerFilter) => gpsDriveGet<FleetRecurringExpense[]>(`${BASE}/recurring-expenses${qs(owner)}`),
  updateRecurringExpense: (owner: FleetOwnerFilter, id: string, body: object) =>
    gpsDrivePut<FleetRecurringExpense>(`${BASE}/recurring-expenses/${id}${qs(owner)}`, body),
  deleteRecurringExpense: (owner: FleetOwnerFilter, id: string) => gpsDriveDelete(`${BASE}/recurring-expenses/${id}${qs(owner)}`),
  expenseCategories: (owner: FleetOwnerFilter) =>
    gpsDriveGet<{ items: FleetExpenseCategory[] }>(`${BASE}/expense-categories${qs(owner)}`),
  addExpenseCategory: (owner: FleetOwnerFilter, label: string) =>
    gpsDrivePost<{ items: FleetExpenseCategory[] }>(`${BASE}/expense-categories${qs(owner)}`, { label }),
  removeExpenseCategory: (owner: FleetOwnerFilter, key: string) =>
    gpsDriveDelete<{ items: FleetExpenseCategory[] }>(`${BASE}/expense-categories/${key}${qs(owner)}`),
  maintenanceServices: (owner: FleetOwnerFilter) =>
    gpsDriveGet<{ items: FleetMaintenanceService[]; isDefault: boolean }>(`${BASE}/maintenance-services${qs(owner)}`),
  saveMaintenanceServices: (owner: FleetOwnerFilter, items: FleetMaintenanceService[] | null) =>
    gpsDrivePut<{ items: FleetMaintenanceService[]; isDefault: boolean }>(
      `${BASE}/maintenance-services${qs(owner)}`,
      items === null ? { reset: true } : { items },
    ),
  // Km atual (odômetro do rastreador ou acumulado do GPS) — Perfil →
  // Manutenção no app (2026-10-14).
  odometer: (owner: FleetOwnerFilter, deviceId: string) =>
    gpsDriveGet<{ deviceId: number; km: number | null }>(`${BASE}/vehicles/${deviceId}/odometer${qs(owner)}`),
  // Lápis da aba Veículos: km (hodômetro) e horímetro do rastreador.
  setOdometer: (owner: FleetOwnerFilter, deviceId: string, body: { km?: number | null; hours?: number | null }) =>
    gpsDrivePut<{ deviceId: number; km: number | null; hours: number | null }>(
      `${BASE}/vehicles/${deviceId}/odometer${qs(owner)}`,
      body,
    ),
  saveAsaasKey: (owner: FleetOwnerFilter, apiKey: string | null) =>
    gpsDrivePut<FleetSettings>(`${BASE}/settings/asaas${qs(owner)}`, { apiKey }),
  driverContractTemplates: (owner: FleetOwnerFilter, driverId: string) =>
    gpsDriveGet<FleetContractTemplate[]>(`${BASE}/drivers/${driverId}/contract-templates${qs(owner)}`),
  driverContracts: (owner: FleetOwnerFilter, driverId: string) =>
    gpsDriveGet<FleetDriverContract[]>(`${BASE}/drivers/${driverId}/contracts${qs(owner)}`),
  createDriverContract: (owner: FleetOwnerFilter, driverId: string, body: CreateFleetDriverContract) =>
    gpsDrivePost<FleetDriverContract>(`${BASE}/drivers/${driverId}/contracts${qs(owner)}`, body),
  deleteDriverContract: (owner: FleetOwnerFilter, driverId: string, id: string) =>
    gpsDriveDelete<void>(`${BASE}/drivers/${driverId}/contracts/${id}${qs(owner)}`),
  // Contratante/testemunhas + assinatura por link (2026-10-26).
  contractSettings: (owner: FleetOwnerFilter) => gpsDriveGet<FleetContractSettings>(`${BASE}/contract-settings${qs(owner)}`),
  saveContractSettings: (owner: FleetOwnerFilter, body: FleetContractSettings) =>
    gpsDrivePut<FleetContractSettings>(`${BASE}/contract-settings${qs(owner)}`, body),
  allContracts: (owner: FleetOwnerFilter) => gpsDriveGet<FleetContractWithDriver[]>(`${BASE}/contracts${qs(owner)}`),
  renewContractLink: (owner: FleetOwnerFilter, driverId: string, id: string) =>
    gpsDrivePost<FleetDriverContract>(`${BASE}/drivers/${driverId}/contracts/${id}/signing-link${qs(owner)}`, {}),
  sendContractLink: (owner: FleetOwnerFilter, driverId: string, id: string, url: string) =>
    gpsDrivePost<{ sent: boolean }>(`${BASE}/drivers/${driverId}/contracts/${id}/send-whatsapp${qs(owner)}`, { url }),
  driverBilling: (owner: FleetOwnerFilter, driverId: string) =>
    gpsDriveGet<FleetDriverBilling>(`${BASE}/drivers/${driverId}/billing${qs(owner)}`),
  createDriverSubscription: (owner: FleetOwnerFilter, driverId: string, body: CreateFleetDriverSubscription) =>
    gpsDrivePost<FleetDriverSubscription>(`${BASE}/drivers/${driverId}/subscriptions${qs(owner)}`, body),
  cancelDriverSubscription: (owner: FleetOwnerFilter, driverId: string, id: string) =>
    gpsDrivePost<FleetDriverSubscription>(`${BASE}/drivers/${driverId}/subscriptions/${id}/cancel${qs(owner)}`, {}),
};

export const FLEET_DRIVER_DOC_CATEGORIES = [
  { key: "cnh", label: "CNH" },
  { key: "documento_pessoal", label: "Documento pessoal (RG/CPF)" },
  { key: "comprovante_residencia", label: "Comprovante de residência" },
  { key: "contrato", label: "Contrato assinado" },
  { key: "outro", label: "Outro" },
] as const;

export interface FleetDriverDocument {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  category: string;
  url: string;
  createdAt: string;
}

export interface FleetContractTemplate {
  id: string;
  title: string;
  content: string;
  updatedAt: string;
}

export interface FleetSettings {
  asaas: { hasApiKey: boolean; keyHint: string | null };
}

export type FleetContractPeriod = "semanal" | "quinzenal" | "mensal" | "unico";

export interface FleetDriverContract {
  id: string;
  templateId: string | null;
  title: string;
  content: string;
  traccarDeviceId: number | null;
  amount: number | null;
  period: FleetContractPeriod | null;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  createdAt: string;
  driverId: string;
  party: FleetContractParty | null;
  witnesses: FleetContractWitness[];
  signingToken: string | null;
  signingLinkGeneratedAt: string | null;
  signedAt: string | null;
  signedByName: string | null;
  signedLatitude: number | null;
  signedLongitude: number | null;
  signedPhotoUrl: string | null;
  signedTimeSource: string | null;
  signedIp: string | null;
}

export interface FleetContractWithDriver extends FleetDriverContract {
  driver: Pick<FleetDriver, "id" | "name" | "cpf" | "phone" | "email" | "cnhNumber" | "cnhCategory" | "cnhExpiresAt">;
}

// Configurações → Contrato: quem contrata o motorista (PF com CPF ou PJ com
// CNPJ) e as testemunhas.
export interface FleetContractParty {
  personType: "pf" | "pj";
  name: string;
  document: string;
  representativeName: string | null;
  representativeCpf: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
}
export interface FleetContractWitness {
  name: string;
  document: string;
}
export interface FleetContractSettings {
  party: FleetContractParty | null;
  witnesses: FleetContractWitness[];
}

// Link público da assinatura do motorista (mesmo wizard do contrato da empresa).
export function fleetContractSigningUrl(token: string): string {
  return `${window.location.origin}/contrato/assinar/m/${token}`;
}

export interface CreateFleetDriverContract {
  templateId: string;
  traccarDeviceId: number | null;
  vehicleLabel: string | null;
  amount: number | null;
  period: FleetContractPeriod | null;
  startDate: string;
  endDate: string | null;
  notes: string | null;
}

export type FleetSubscriptionCycle = "WEEKLY" | "BIWEEKLY" | "MONTHLY" | "BIMONTHLY" | "QUARTERLY" | "SEMIANNUALLY" | "YEARLY";
export type FleetBillingType = "BOLETO" | "PIX" | "CREDIT_CARD" | "UNDEFINED";

export interface FleetDriverSubscription {
  id: string;
  asaasSubscriptionId: string;
  cycle: FleetSubscriptionCycle;
  billingType: FleetBillingType;
  value: number;
  nextDueDate: string;
  description: string | null;
  status: "ativa" | "cancelada";
  canceledAt: string | null;
  createdAt: string;
}

export interface CreateFleetDriverSubscription {
  cycle: FleetSubscriptionCycle;
  billingType: FleetBillingType;
  value: number;
  nextDueDate: string;
  description?: string;
  interestValue?: number;
  fineValue?: number;
}

export interface FleetDriverInvoice {
  id: string;
  status: "pendente" | "paga" | "vencida" | "estornada" | "outro";
  billingType: string;
  value: number;
  dueDate: string;
  paymentDate: string | null;
  description: string | null;
  invoiceUrl: string | null;
  subscription: string | null;
}

export interface FleetDriverBilling {
  asaasEnabled: boolean;
  asaasCustomerId: string | null;
  subscriptions: FleetDriverSubscription[];
  invoices: FleetDriverInvoice[];
  invoicesError: string | null;
}
